import styled, { x } from '@xstyled/styled-components';
import React, { forwardRef } from 'react';
import { animated } from '@react-spring/web';
import { appTheme } from '~/shell/theme';
import { useToastStore } from './toastStore';
import { ToastDefinition, ToastSeverity } from './types';
import { gettext, t } from 'ttag';
import { useTranslationLoaded } from '~/lib/translations';
import { AnyStyledComponent } from 'styled-components';
import Pic from './error.png';

const bgColor = {
  [ToastSeverity.Error]: appTheme.colors.redLight,
  [ToastSeverity.Warning]: appTheme.colors.yellowMed,
  [ToastSeverity.Success]: appTheme.colors.greenMed
};

const borderColor = {
  [ToastSeverity.Error]: appTheme.colors.redDark,
  [ToastSeverity.Warning]: appTheme.colors.yellowDark,
  [ToastSeverity.Success]: appTheme.colors.greenDark
};

const AnimatedDiv = animated.div as AnyStyledComponent;
const Container = styled(AnimatedDiv)<{ bg: string; br: string }>`
  min-height: 100px;
  position: relative;
  border-radius: md;
  border-width: 1px;
  overflow: hidden;
  margin-top: md;
  box-shadow: 5px 5px 10px #94918f;
  color: #000000;
  background-color: ${p => p.bg};
  border-color: ${p => p.br};
  &:last-child {
    margin-bottom: md;
  }
`;

const Image = styled.img`
  position: absolute;
  width: 25px;
`;

function translateQueuedMessage(message: string) {
  // disable ttag -- Source keys are extracted where each error is created.
  return gettext(message);
}

export const Toast = forwardRef<
  HTMLDivElement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { toast: ToastDefinition; style: any }
>(({ toast, style }, ref) => {
  const remove = useToastStore(s => s.remove);
  // A language bundle can finish loading after an error was queued.
  useTranslationLoaded();

  return (
    <Container
      style={{ ...style, height: 'auto' }}
      bg={bgColor[toast.severity]}
      br={borderColor[toast.severity]}
      role={'alertdialog'}
      data-test-id="toast"
    >
      <x.div
        ref={ref}
        p="lg"
        boxSizing="border-box"
        style={{ padding: '20px 12px' }}
      >
        <x.button
          autoFocus
          aria-label={t`Close popup`}
          position="absolute"
          top="4px"
          right="16px"
          border="none"
          background="transparent"
          marginTop="10px"
          onClick={() => remove(toast.id!)}
          fontSize="xxl"
          p="0"
          h="17px"
          display="flex"
          alignItems="center"
          overflow="hidden"
          userSelect="none"
          data-test-id="close-toast-button"
        >
          <span style={{ color: borderColor[toast.severity] }}>&times;</span>
        </x.button>
        <Image src={Pic} />
        <x.div style={{ marginLeft: '36px', whiteSpace: 'pre-line' }}>
          <div>
            <b>{t`Error`}</b>
          </div>
          {translateQueuedMessage(toast.message)}
        </x.div>
      </x.div>
    </Container>
  );
});
